/**
 * Employees Query Hooks — cached-instant rendering for EmployeesScreen.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeesApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

interface EmployeesListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  organizational_unit?: number;
  search?: string;
  year?: number;
  // Source the directory from SSO (the headcount source of truth) instead of
  // the local Employee table — keeps the list in sync with the جهتي card count.
  includeSso?: boolean;
}

interface EmployeesListResponse {
  results: any[];
  count: number;
}

export function useEmployeesQuery(params?: EmployeesListParams) {
  return useQuery({
    queryKey: queryKeys.employees.list(params),
    queryFn: async (): Promise<EmployeesListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.organizational_unit)
        queryParams.organizational_unit = params.organizational_unit;
      if (params?.search) queryParams.search = params.search;
      if (params?.year) queryParams.year = params.year;
      if (params?.includeSso) queryParams.include_sso = 1;

      return await employeesApi.list(queryParams);
    },
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useEmployeeDetailQuery(employeeId: number) {
  return useQuery({
    queryKey: queryKeys.employees.detail(employeeId),
    queryFn: () => employeesApi.get(employeeId),
    enabled: !!employeeId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useDeleteEmployeeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: number) => employeesApi.delete(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
    },
  });
}

export default useEmployeesQuery;
